import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { useAuth } from "@/auth/AuthContext";
import { LEAD_SOURCES, POTENTIALS, SOURCE_COLORS, formatINR } from "@/lib/constants";
import { CheckCircle2, Loader2, Gift, UserPlus, IndianRupee, FileUp, Plus, Pencil, Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const inputCls =
  "w-full h-11 px-3.5 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500";
const labelCls = "text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-1.5 block";
const triggerCls =
  "h-11 rounded-lg border-slate-200 bg-white text-sm shadow-none data-[placeholder]:text-slate-400 focus:ring-2 focus:ring-blue-500/30";
const POT_DOT = { Hot: "bg-rose-500", High: "bg-emerald-500", Medium: "bg-amber-500", Low: "bg-slate-400" };

const emptyForm = {
  company_name: "",
  contact_person: "",
  location: "",
  phone: "",
  lead_source: "IndiaMART",
  product: "",
  quantity: 1,
  potential: "High",
  is_corporate_gifting: false,
  assigned_rep: "",
  sales_value: 0,
};

export default function LeadEntry({ products = [], team = [], leads = [], initialMode, onCreated, onDone }) {
  const [mode] = useState(initialMode || "lead"); // fixed per entry point
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [proforma, setProforma] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extraItems, setExtraItems] = useState([]);
  const [editClosing, setEditClosing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const { isOwner } = useAuth();

  const closing = mode === "closing";
  const favorites = products.filter((p) => p.favorite);
  const directClosings = leads.filter((l) => l.direct_closing);
  const directTotal = directClosings.reduce((s, l) => s + (Number(l.sales_value) || 0), 0);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setRow = (i, patch) =>
    setExtraItems((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const removeClosing = async (id) => {
    if (!window.confirm("Delete this direct closing and its tracked order?")) return;
    try {
      await axios.delete(`${API}/leads/${id}`);
      toast.success("Direct closing deleted");
      onCreated && onCreated();
    } catch {
      toast.error("Delete failed");
    }
  };

  const openEditClosing = (l) => {
    setEditClosing(l);
    setEditForm({
      company_name: l.company_name || "",
      product: l.product || "",
      sales_value: l.sales_value || 0,
      assigned_rep: l.assigned_rep || "",
    });
  };

  const saveEditClosing = async () => {
    if (!editForm.company_name) {
      toast.error("Company is required");
      return;
    }
    try {
      await axios.patch(`${API}/leads/${editClosing.id}`, {
        company_name: editForm.company_name,
        product: editForm.product,
        sales_value: Number(editForm.sales_value) || 0,
        assigned_rep: editForm.assigned_rep || null,
      });
      toast.success("Direct closing updated");
      setEditClosing(null);
      setEditForm(null);
      onCreated && onCreated();
    } catch {
      toast.error("Update failed");
    }
  };

  const handleProforma = async (file) => {
    setProforma(file || null);
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/proforma/extract`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = res.data || {};
      setForm((f) => ({
        ...f,
        company_name: d.company_name || f.company_name,
        contact_person: d.contact_person || f.contact_person,
        product: d.product || f.product,
        quantity: d.quantity || f.quantity,
        sales_value: d.sales_value || f.sales_value,
      }));
      if (d.product) {
        setCustomMode(!products.some((p) => p.name.toLowerCase() === String(d.product).toLowerCase()));
      }
      toast.success("Details read from the proforma — please review");
    } catch {
      toast.error("Couldn't read the proforma — enter details manually");
    } finally {
      setExtracting(false);
    }
  };

  const handleProductSelect = (v) => {
    if (v === "__custom__") {
      setCustomMode(true);
      update("product", "");
    } else {
      setCustomMode(false);
      update("product", v);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.product) {
      toast.error("Company and product are required");
      return;
    }
    if (!closing && (!form.location || !form.phone)) {
      toast.error("Location and phone are required");
      return;
    }
    if (closing && (!form.sales_value || Number(form.sales_value) <= 0)) {
      toast.error("Enter the closing sale value");
      return;
    }
    setSubmitting(true);
    try {
      const productName = form.product.trim();
      if (
        customMode &&
        productName &&
        !products.some((p) => p.name.toLowerCase() === productName.toLowerCase())
      ) {
        try {
          await axios.post(`${API}/products`, { name: productName, favorite: true });
        } catch {
          /* ignore duplicate */
        }
      }
      for (const it of extraItems) {
        const pn = (it.product || "").trim();
        if (it.custom && pn && !products.some((p) => p.name.toLowerCase() === pn.toLowerCase())) {
          try {
            await axios.post(`${API}/products`, { name: pn, favorite: true });
          } catch {
            /* ignore */
          }
        }
      }
      const payload = {
        company_name: form.company_name,
        contact_person: form.contact_person,
        location: form.location,
        phone: form.phone,
        lead_source: closing ? "Direct" : form.lead_source,
        product: productName,
        quantity: Number(form.quantity) || 1,
        potential: form.potential,
        is_corporate_gifting: form.is_corporate_gifting,
      };
      if (closing) {
        payload.direct_closing = true;
        payload.status = "Deal Won";
        payload.sales_value = Number(form.sales_value) || 0;
        payload.assigned_rep = form.assigned_rep || null;
      }
      payload.items = [
        { product: productName, quantity: Number(form.quantity) || 1 },
        ...extraItems
          .filter((x) => x.product.trim())
          .map((x) => ({ product: x.product.trim(), quantity: Number(x.quantity) || 1 })),
      ];
      await axios.post(`${API}/leads`, payload);
      toast.success(closing ? "Closing recorded — added to Current Orders" : "Lead added to the library", {
        description: `${form.company_name} · ${productName}`,
      });
      setForm(emptyForm);
      setProforma(null);
      setExtraItems([]);
      await onCreated();
      onDone && onDone();
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSubmitting(false);
      setCustomMode(false);
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-3xl w-full mx-auto">
      <header className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.14em] text-blue-600 font-semibold">
          {closing ? "Direct closing" : "New opportunity"}
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-1">
          {closing ? "Record a deal closed directly" : "Add a fresh lead to the pipeline"}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {closing
            ? "For orders closed straight with a client — it lands as a won deal in revenue & commission."
            : "Submit once — it lands in the Lead Library and metrics update in real time."}
        </p>
      </header>

      <form
        onSubmit={submit}
        data-testid="lead-entry-form"
        className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {!closing ? (
            <>
              <div>
                <label className={labelCls}>Lead source *</label>
                <Select value={form.lead_source} onValueChange={(v) => update("lead_source", v)}>
                  <SelectTrigger data-testid="input-lead-source" className={triggerCls}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg">
                    {LEAD_SOURCES.map((s) => (
                      <SelectItem key={s} value={s} className="rounded-md py-2">
                        <span className="flex items-center gap-2.5">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: SOURCE_COLORS[s] }} />
                          {s}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={labelCls}>Lead potential *</label>
                <Select value={form.potential} onValueChange={(v) => update("potential", v)}>
                  <SelectTrigger data-testid="input-potential" className={triggerCls}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg">
                    {POTENTIALS.map((s) => (
                      <SelectItem key={s} value={s} className="rounded-md py-2">
                        <span className="flex items-center gap-2.5">
                          <span className={`w-2.5 h-2.5 rounded-full ${POT_DOT[s]}`} />
                          {s}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className={labelCls}>Sale value (₹) *</label>
                <input
                  data-testid="input-sale-value"
                  type="number"
                  min="0"
                  value={form.sales_value}
                  onChange={(e) => update("sales_value", e.target.value)}
                  placeholder="e.g. 118000 (incl. GST)"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Closed by (rep)</label>
                <Select
                  value={form.assigned_rep || "__unassigned__"}
                  onValueChange={(v) => update("assigned_rep", v === "__unassigned__" ? "" : v)}
                >
                  <SelectTrigger data-testid="input-assigned-rep" className={triggerCls}>
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__unassigned__">Unassigned</SelectItem>
                    {team.map((t) => (
                      <SelectItem key={t.id} value={t.name}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div className="sm:col-span-2">
            <label className={labelCls}>Company name *</label>
            <input
              data-testid="input-company"
              value={form.company_name}
              onChange={(e) => update("company_name", e.target.value)}
              placeholder="e.g. Aster Retail Pvt Ltd"
              className={inputCls}
            />
          </div>

          <div>
            <label className={labelCls}>Point of contact</label>
            <input
              data-testid="input-contact"
              value={form.contact_person}
              onChange={(e) => update("contact_person", e.target.value)}
              placeholder="Person's name"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Phone number{closing ? "" : " *"}</label>
            <input
              data-testid="input-phone"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              placeholder="+91 98765 43210"
              className={inputCls}
            />
          </div>

          <div className="sm:col-span-2">
            <label className={labelCls}>Full address / location{closing ? "" : " *"}</label>
            <input
              data-testid="input-location"
              value={form.location}
              onChange={(e) => update("location", e.target.value)}
              placeholder="City, State"
              className={inputCls}
            />
          </div>

          <div>
            <label className={labelCls}>Order quantity</label>
            <input
              data-testid="input-quantity"
              type="number"
              min="1"
              value={form.quantity}
              onChange={(e) => update("quantity", e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Product *</label>
            <Select
              value={customMode ? "__custom__" : form.product || ""}
              onValueChange={handleProductSelect}
            >
              <SelectTrigger data-testid="input-product" className={triggerCls}>
                <SelectValue placeholder={favorites.length ? "Select a product…" : "Pick custom below"} />
              </SelectTrigger>
              <SelectContent className="rounded-lg max-h-[300px]">
                {favorites.map((p) => (
                  <SelectItem key={p.id} value={p.name} className="rounded-md py-2">
                    {p.name}
                  </SelectItem>
                ))}
                <SelectItem value="__custom__" className="rounded-md py-2 text-blue-700 font-semibold">
                  + Custom / other product…
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          {customMode && (
            <div className="sm:col-span-2">
              <input
                data-testid="input-custom-product"
                value={form.product}
                onChange={(e) => update("product", e.target.value)}
                placeholder="Type the custom product name — it will be saved to your catalog"
                className={inputCls}
              />
            </div>
          )}

          {extraItems.map((it, i) => (
            <div key={i} className="sm:col-span-2 flex gap-2 items-start">
              <div className="flex-1">
                <Select
                  value={it.custom ? "__custom__" : it.product || ""}
                  onValueChange={(v) =>
                    v === "__custom__" ? setRow(i, { custom: true, product: "" }) : setRow(i, { custom: false, product: v })
                  }
                >
                  <SelectTrigger data-testid={`extra-product-${i}`} className={triggerCls}>
                    <SelectValue placeholder="Select a product…" />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg max-h-[300px]">
                    {favorites.map((p) => (
                      <SelectItem key={p.id} value={p.name} className="rounded-md py-2">
                        {p.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="__custom__" className="rounded-md py-2 text-blue-700 font-semibold">
                      + Custom / other product…
                    </SelectItem>
                  </SelectContent>
                </Select>
                {it.custom && (
                  <input
                    data-testid={`extra-custom-${i}`}
                    value={it.product}
                    onChange={(e) => setRow(i, { product: e.target.value })}
                    placeholder="Type custom product name — saved to catalog"
                    className={`${inputCls} mt-2`}
                  />
                )}
              </div>
              <input
                type="number"
                min="1"
                value={it.quantity}
                onChange={(e) => setRow(i, { quantity: e.target.value })}
                className="w-24 h-11 px-3 rounded-lg border border-slate-200 text-sm"
              />
              <button
                type="button"
                onClick={() => setExtraItems(extraItems.filter((_, j) => j !== i))}
                className="px-2 h-11 text-slate-400 hover:text-rose-600"
              >
                ✕
              </button>
            </div>
          ))}
          <div className="sm:col-span-2">
            <button
              type="button"
              data-testid="add-product-row"
              onClick={() => setExtraItems([...extraItems, { product: "", quantity: 1, custom: false }])}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700"
            >
              <Plus size={15} /> Add another product
            </button>
          </div>

          {closing && (
            <div className="sm:col-span-2">
              <label className={labelCls}>Proforma / invoice — auto-fill from upload</label>
              <label
                data-testid="input-proforma-label"
                className="flex items-center gap-2.5 px-4 h-12 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-600 cursor-pointer hover:bg-slate-100"
              >
                {extracting ? <Loader2 size={16} className="animate-spin text-blue-500" /> : <FileUp size={16} className="text-slate-400" />}
                {extracting ? "Reading proforma…" : proforma ? proforma.name : "Upload a proforma/invoice (image or PDF) to auto-fill the fields"}
                <input
                  data-testid="input-proforma"
                  type="file"
                  accept="image/*,application/pdf,.pdf"
                  className="hidden"
                  onChange={(e) => handleProforma(e.target.files[0] || null)}
                />
              </label>
              <p className="text-[11px] text-slate-400 mt-1">JPG, PNG or PDF · we read the details and don't store the file.</p>
            </div>
          )}

          <div className="sm:col-span-2">
            <button
              type="button"
              data-testid="input-corporate-gifting"
              onClick={() => update("is_corporate_gifting", !form.is_corporate_gifting)}
              className={`w-full flex items-center justify-between gap-3 px-4 h-14 rounded-lg border ${
                form.is_corporate_gifting ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white hover:bg-slate-50"
              }`}
              style={{ transition: "background-color 160ms ease, border-color 160ms ease" }}
            >
              <span className="flex items-center gap-2.5 text-left">
                <Gift size={18} className={form.is_corporate_gifting ? "text-amber-600" : "text-slate-400"} />
                <span>
                  <span className="block text-sm font-semibold text-slate-900">Corporate gifting company</span>
                  <span className="block text-xs text-slate-500">Priority client · eligible for discounted pricing</span>
                </span>
              </span>
              <span
                className={`relative w-11 h-6 rounded-full ${form.is_corporate_gifting ? "bg-amber-500" : "bg-slate-300"}`}
                style={{ transition: "background-color 160ms ease" }}
              >
                <span
                  className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow ${
                    form.is_corporate_gifting ? "left-[22px]" : "left-0.5"
                  }`}
                  style={{ transition: "left 160ms ease" }}
                />
              </span>
            </button>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-6">
          <div className="text-xs text-slate-500 flex items-center gap-1.5">
            <CheckCircle2 size={14} className="text-emerald-500" />
            {closing ? "Counts instantly in revenue & commission" : "Instantly synced across dashboard & library"}
          </div>
          <button
            data-testid="submit-lead-button"
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 px-5 h-10 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold text-sm rounded-md shadow-sm"
            style={{ transition: "background-color 180ms ease" }}
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : closing ? <IndianRupee size={16} /> : <UserPlus size={16} />}
            {submitting ? "Saving…" : closing ? "Record closing" : "Submit lead"}
          </button>
        </div>
      </form>

      {closing && (
        <div className="mt-6 bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="direct-closing-tracker">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading text-lg font-semibold text-slate-800">Recorded direct closings</h2>
              <p className="text-xs text-slate-500">Track direct orders against your sales target</p>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                Total ({directClosings.length})
              </div>
              <div className="font-heading text-xl font-bold text-emerald-700">{formatINR(directTotal)}</div>
            </div>
          </div>
          {directClosings.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No direct closings recorded yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {directClosings.slice(0, 15).map((l) => (
                <li
                  key={l.id}
                  data-testid={`direct-closing-${l.id}`}
                  className="py-2.5 flex items-center justify-between gap-3 text-sm"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate">{l.company_name}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {l.product} · {l.assigned_rep || "Unassigned"} ·{" "}
                      {new Date(l.created_at).toLocaleDateString("en-IN")}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="font-semibold text-slate-900">{formatINR(l.sales_value || 0)}</span>
                    <button data-testid={`edit-closing-${l.id}`} onClick={() => openEditClosing(l)} title="Edit closing" className="p-1.5 text-slate-400 hover:text-blue-600" style={{ transition: "color 160ms ease" }}><Pencil size={15} /></button>
                    {isOwner && (
                      <button data-testid={`delete-closing-${l.id}`} onClick={() => removeClosing(l.id)} title="Delete closing" className="p-1.5 text-slate-400 hover:text-rose-600" style={{ transition: "color 160ms ease" }}><Trash2 size={15} /></button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Dialog open={!!editClosing} onOpenChange={(v) => { if (!v) { setEditClosing(null); setEditForm(null); } }}>
        <DialogContent data-testid="edit-closing-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit direct closing</DialogTitle>
          </DialogHeader>
          {editForm && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className={labelCls}>Company</label>
                <input data-testid="edit-closing-company" value={editForm.company_name} onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Product(s)</label>
                <input data-testid="edit-closing-product" value={editForm.product} onChange={(e) => setEditForm({ ...editForm, product: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Sale value ₹</label>
                <input data-testid="edit-closing-value" type="number" min="0" value={editForm.sales_value} onChange={(e) => setEditForm({ ...editForm, sales_value: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Closed by (rep)</label>
                <Select value={editForm.assigned_rep || "__unassigned__"} onValueChange={(v) => setEditForm({ ...editForm, assigned_rep: v === "__unassigned__" ? "" : v })}>
                  <SelectTrigger data-testid="edit-closing-rep" className={triggerCls}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__unassigned__">Unassigned</SelectItem>
                    {team.map((t) => (<SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => { setEditClosing(null); setEditForm(null); }} className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
            <button data-testid="edit-closing-save" onClick={saveEditClosing} className="h-10 px-4 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">Save changes</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
